import React, {MouseEvent, useCallback, useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {useAppState, useDsManager} from "@stores/AppStateContext";

import {UserRole} from "@stores/UserSessionState";

import {MODALS} from "../modal-names";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";
import {enqueueSnackbar} from "notistack";

import {ModalBase} from "../components/ModalBase";
import {InviteLink} from "../../../elements/InviteLink";
import {Button} from "../../../components/Button";
import {
    getInviteLink,
    getInviteLinkFromClient,
    resetInviteLink,
    setInviteLinkUserRole
} from "../../../../actions/invite";
import {logErrorToSentry} from "../../../../utils/errors/logErrorToSentry";
import {getUserRoleOptions} from "../../Members/UserRoleSelect";

import {useCrudServices} from "../../../../platform/hooks/useServiceCombinations";

import {Select} from '../../../components/form/Select';


import './Invite.css';


export const Invite = observer(() => {
    const appState = useAppState();
    const dsManager = useDsManager();
    const rootStore = appState.rootStore;
    const userState = appState.userState!;
    const crudServices = useCrudServices();

    const isMobile = screenSizeProvider.view !== 'desktop';
    const [isLoading, setLoading] = useState(false);
    const [inviteLink, setInviteLink] = useState<string>('');
    const [isConfirmReset, setConfirmReset] = useState(false);

    useEffect(() => {
        getInviteLink(userState.dsManager!).then((result) => {
            setInviteLink(result);
        });
    }, []);

    const resetLink = (
        isMobile
            ? intl.get('invite.reset-link-btn').d('reset the link')
            : `<button class="Invite_reset body-medium btn btn--sm btn--soft-neutral">${intl.get('invite.reset-link-btn').d('reset the link')}</button>`
    )

    const btnContainerClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
        /* @ts-ignore */
        if (event.target.tagName !== 'BUTTON') {
            return;
        }
        setConfirmReset(!isConfirmReset);
    }, [isConfirmReset]);

    const handleClose = useCallback(() => {
        appState.setModal(null);
        appState.setContextMenu(null);
    }, []);

    const resetLinkCb = useCallback(() => {
        setLoading(true);
        resetInviteLink(crudServices, userState, rootStore).then((client) => {
            setInviteLink(getInviteLinkFromClient(client));
            setLoading(false);
            setConfirmReset(false);
        }).catch(logErrorToSentry);
    }, [])

    const activeModal = appState.topModal;
    const userRoles = getUserRoleOptions();
    const currentDefaultRole = dsManager?.clientAR.row?.userRole;
    const [defaultUserRole, setDefaultUserRole] = useState<string | null>(currentDefaultRole || 'reader');

    const handleInviteRoleChange = useCallback((value: string | null) => {
        setDefaultUserRole(value);
        setInviteLinkUserRole(crudServices, userState, rootStore, value as UserRole).then(() => {
            enqueueSnackbar({
                message: intl.get('invite.initialRoleChangeSuccess').d('Initial role changed successfully.'),
                variant: 'success',
            });
        }).catch((error: unknown) => {
            enqueueSnackbar({
                message: intl.get('user-role.general_error').d(`Something went wrong, but we're actively working to fix this issue. In the meantime, please feel free to contact us.`),
                variant: 'error',
            });
            logErrorToSentry(error as Error);
        });
    }, []);

    const renderRoleChangeContent = () => (
        <div className="Invite_bottom_part">
            <div className="Invite_bottom_part__text">
                <span>
                    {intl.get('invite.role-text').d('All invitees via this link will')}
                </span>
                &nbsp;
                <span className="body-large-bold">
                    {intl.get('invite.role-text-bold').d('be assigned the role:')}
                </span>
            </div>
            {!isMobile
                ? (
                    <Select
                        selectedValue={defaultUserRole}
                        options={userRoles}
                        size={"medium"}
                        onChange={handleInviteRoleChange}
                        maxWidth={240}
                        isFullWidth={false}
                        iconSize={20}
                        withBorder={true}
                        className={"InviteRole-select"}
                    />
                )
                : (
                    <Select
                        withBorder={true}
                        shouldForcePlace={true}
                        selectedValue={defaultUserRole}
                        options={userRoles}
                        size={"medium"}
                        onChange={handleInviteRoleChange}
                        maxWidth={240}
                        isFullWidth={true}
                        iconSize={20}
                        className={"InviteRole-select"}
                    />
                )}
        </div>
    )

    const renderResetLinkContent = () => (
        <div className='Invite_reset-confirm'>
            <div className='Invite_reset-confirm__text'>
                {intl.get('invite.confirm_reset').d('Are you sure you want to reset the invite link? Your old one will no longer be usable.')}
            </div>
            <div className='Invite_reset-buttons'>
                <Button
                    className='Invite_reset-cancel'
                    type="neutral"
                    style="plain"
                    text={intl.get('common.cancel').d('Cancel')}
                    size='md'
                    onClick={() => setConfirmReset(false)}
                />
                <Button
                    className='Invite_reset-confirm-button'
                    type="primary"
                    loading={isLoading}
                    text={intl.get('common.reset').d('Reset')}
                    onClick={resetLinkCb}
                    size='lg'
                />
            </div>
        </div>
    );

    return (
        <ModalBase
            open={activeModal === MODALS.INVITE}
            header={intl.get('invite.title').d('Invite people by link')}
            onClose={handleClose}
            minWidth={580}
            maxWidth={680}
        >
            {isMobile
                ? renderRoleChangeContent()
                : null
            }
            <div className='Invite_text'>
                {intl.get('invite.text').d('Use this secret link to invite people to this workspace.')}
            </div>
            <InviteLink key={inviteLink} inviteLink={inviteLink} className='Invite_link'/>
            <div className='Invite_reset-text body-medium'
                 onClick={btnContainerClick}
            >
                {intl.getHTML('invite.reset-text', {resetLink: resetLink}).d(`You can{resetLink}for all space members to generate a new invite link.`)}
            </div>
            {
                (isConfirmReset && !isMobile)
                    ? renderResetLinkContent()
                    : null
            }
            {!isMobile
                ? renderRoleChangeContent()
                : null
            }
            {
                isMobile
                    ? (
                        <>
                            <Button
                                className={'Invite_reset-btn title-small'}
                                type={'neutral'}
                                style={'soft'}
                                size={'md'}
                                text={intl.get('invite.reset-link-btn').d('reset the link')}
                                onClick={() => {
                                    setConfirmReset(!isConfirmReset);
                                }}
                            />
                            {isConfirmReset ? renderResetLinkContent() : null}
                        </>
                    )
                    : null
            }
        </ModalBase>
    );
});
